import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {useState, useEffect} from 'react';
import {v4 as uuidv4} from 'uuid';

import {Sequence} from 'components/Sequence';
import {EventConstants} from 'constants/EventConstants';
import {User} from 'constants/UserConstants';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AppDispatcher from 'lib/Dispatcher';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {AaSequenceVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {areAaPartFeaturesEnabledForAntibody} from 'molbio/aa-sequences/util/aminoAcidPartsUtil';
import {
  CurrentPolymerContentsQuery,
  CurrentPolymerContentsQueryVariables,
} from 'molbio/hooks/__generated/CurrentPolymerContentsQuery';
import {buildSequenceModel, UNEDITABLE_POLYMER_PERMISSIONS} from 'molbio/MolBioUtil';
import notifications from 'notifications';

export const CURRENT_POLYMER_CONTENTS_QUERY = gql`
  query CurrentPolymerContentsQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    polymer(id: $id) {
      ... on SequenceNode {
        id
        name
        sequenceType
        nucleotideType
        partsJson
        experimentalCurrentVersion {
          dbId
          bases
          circular
          startIndex
          annotationsJson
          sequencePrimersJson
          primerPairsJson
          translationGroupsJson
          translationsJson
          modifiedNucleotidesJson
        }
      }
      ... on AaSequenceNode {
        id
        name
        displayLabel
        createdAt
        modifiedAt
        noteJson
        tagSchema {
          id
        }
        experimentalCurrentVersion {
          dbId
          aminoAcids
          name
          annotations {
            dbId
            annotationId
            annotationType
            name
            start
            end
            color
            notes
          }
          createdAt
          modifiedAt
          parentId
          partsJson
          ...HasCreatorGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('HasCreator').fragment}
`;

/**
 * Hook to create a transient Backbone model for an existing polymer (Sequence or Protein) by API Id.
 *
 * The contents of the polymer are populated using the current version, queried by GQL. While the data
 * is being fetched, the hook will return `null`. If the GQL cache is updated elsewhere for the entity,
 * the Backbone model will be reconstructed but otherwise the same class instance be returned across renders.
 */
export default function useTransientCurrentPolymerModel(polymerId: string): Sequence | AaSequence | null {
  const [polymer, setPolymer] = useState<Sequence | AaSequence | null>(null);
  const currentPolymerContentsResults = useBenchlingQuery<
    CurrentPolymerContentsQuery,
    CurrentPolymerContentsQueryVariables
  >(CURRENT_POLYMER_CONTENTS_QUERY, {variables: {id: polymerId}});
  const data =
    currentPolymerContentsResults.type === QueryResultType.SUCCESS
      ? currentPolymerContentsResults.data
      : undefined;

  useEffect(() => {
    if (data == null) {
      setPolymer(null);
      return noop;
    }
    const polymerNode = data.polymer;
    if (isInaccessible(polymerNode)) {
      notifications.error('You no longer have read permissions for this entity.');
      setPolymer(null);
      return noop;
    } else if (polymerNode.__typename === 'SequenceNode') {
      const sequence = buildSequenceModel(polymerNode, polymerNode.experimentalCurrentVersion);
      setPolymer(sequence);
      return () => {
        sequence.fileState().cleanUp();
      };
    } else {
      const mockId = `${polymerId}_${uuidv4()}`;
      const aaSequence = new AaSequence(
        {
          id: mockId,
          isTransientDisplay: true,
          transientSource: {entityId: polymerId, tagSchemaId: polymerNode.tagSchema?.id ?? null},
        },
        {bypassProxyBlock: true}
      );
      const version: AaSequenceVersion = {
        id: polymerNode.experimentalCurrentVersion.dbId,
        aminoAcids: polymerNode.experimentalCurrentVersion.aminoAcids,
        name: polymerNode.experimentalCurrentVersion.name,
        annotations: polymerNode.experimentalCurrentVersion.annotations.map(
          ({annotationId, annotationType, name, start, end, color, notes}) => ({
            id: annotationId,
            type: annotationType,
            name,
            start,
            end,
            color,
            notes,
          })
        ),
        createdAt: polymerNode.experimentalCurrentVersion.createdAt,
        creator: graphQLToREST('Creator', polymerNode.experimentalCurrentVersion.creator) as User,
        modifiedAt: polymerNode.experimentalCurrentVersion.modifiedAt,
        parentId: polymerNode.experimentalCurrentVersion.parentId,
        structures: [],
        parts: areAaPartFeaturesEnabledForAntibody() ? polymerNode.experimentalCurrentVersion.partsJson : [],
      };
      AppDispatcher.dispatch({
        actionType: EventConstants.AA_SEQUENCE_FETCH_VERSIONS_COMPLETED,
        versions: [version],
        proteinId: mockId,
      });
      AppDispatcher.dispatch({
        actionType: EventConstants.PROTEIN_OPEN_COMPLETED,
        protein: {
          alignments: [],
          linkedAlignments: [],
          archiveRecord: null,
          authors: [],
          bioentityAliasInfos: [],
          created_at: polymerNode.createdAt,
          modified_at: polymerNode.modifiedAt,
          displayLabel: polymerNode.displayLabel,
          editURL: '',
          id: mockId,
          isLatest: true,
          name: polymerNode.name,
          note: polymerNode.noteJson,
          permissions: UNEDITABLE_POLYMER_PERMISSIONS,
          schemaValidationState: null,
          tagSchema: null,
          tags: [],
          version,
        },
        proteinId: mockId,
      });
      setPolymer(aaSequence);
      return () => {
        aaSequence.cleanUp();
      };
    }
  }, [polymerId, data]);

  return polymer;
}
